import * as yup from 'yup';
import { useExperiment, PROMO_CODE_CONTENT_KEY } from '@web/experiments';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
  richTextFromCMSSchema,
} from '../../cms';
import { schema as emailCaptureSchema } from '../../cms/builders/email_capture';

const usePromoCodeVariant = variants => {
  const { value: promoCode } = useExperiment(PROMO_CODE_CONTENT_KEY, '');

  return (
    variants.find(variant => variant.promoCode === promoCode) ??
    variants.find(variant => variant.promoCode === 'default')
  );
};

const useExperimentValues = value => {
  const question = value?.question?.[0];
  let { value: experimentValue } = useExperiment(question?.test_name ?? null);
  experimentValue = experimentValue || 'control';
  let questionVariants = question;
  const experimentVariation = questionVariants?.variants?.find(
    variant => variant.variant_name === experimentValue,
  );

  questionVariants = experimentVariation?.blocks[0] ?? [];
  return questionVariants;
};

const crunchingNumbersContentSchema = yup
  .object({
    images: yup.array(requiredImageFromCMSSchema),
    captions: yup.array(),
    slideDuration: yup.number(),
    headline: yup.string(),
    backgroundColor: yup.string(),
    fontColor: yup.string(),
  })
  .transform(value => ({
    images: value.images,
    captions: value.captions.split('/'),
    slideDuration: Number(value.slideDuration),
    headline: value.headline,
    backgroundColor: value.backgroundColor || undefined,
    fontColor: value.fontColor || undefined,
  }));

const modalSchema = yup
  .object({
    image: nullableImageFromCMSSchema.default(undefined),
    heading: yup.string(),
    description: yup.string(),
    emailCapture: emailCaptureSchema.default(undefined),
    backgroundColor: yup.string(),
  })
  .nullable()
  .default(undefined)
  //* A bloks list is coerced to null before transforms run, so the first blok
  //* has to come off the untouched CMS value; a referenced story arrives as an
  //* object and is used as is.
  .transform((value, modal) => (Array.isArray(modal) ? modal[0] : value))
  .transform(
    value =>
      value && {
        image: value.image,
        heading: value.heading || undefined,
        description: value.description || undefined,
        emailCapture: value.email_capture?.[0],
        backgroundColor: value.background_color || undefined,
      },
  );

const itemSchema = yup.object({
  title: yup.string().required(),
  label: yup.string().required(),
  benefit: yup.string().optional(),
  callout: yup.string().optional(),
  value: yup.string().required(),
  description: richTextFromCMSSchema,
  img: nullableImageFromCMSSchema,
  unavailableText: yup.string(),
  priceSymbol: yup.string().optional(),
  showMoreDetails: yup.bool(),
  pricingInfo: yup.object({
    price: yup.string(),
    discount: yup.string(),
    period: yup.string(),
  }),
  level: yup.string(),
  hidden: yup.bool(),
  carousel_assets: yup.array(nullableImageFromCMSSchema),
  out_of_stock: yup.bool(),
  getNotified: yup.bool(),
  getNotifiedText: yup
    .string()
    .nullable()
    .transform(value => value || undefined),
  modal: modalSchema.default(undefined),
});

export const recommendedPlanSchema = yup
  .object({
    ollieLogo: nullableImageFromCMSSchema,
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    crunchingNumbersContentVariant: yup.object({
      promoCode: yup.string(),
      content: crunchingNumbersContentSchema.required(),
    }),
    showPlumLoadingScreen: yup.boolean(),
    ctaText: yup.string(),
    body: yup.array(),
    subheadline: yup.string(),
    recipeModalsList: yup.array(),
    recipesList: yup
      .array(
        itemSchema
          .from('display_name', 'title')
          .from('slug', 'value')
          .from('image', 'img')
          .from('unavailable_text', 'unavailableText')
          .from('show_more_details', 'showMoreDetails')
          .from('price_symbol', 'priceSymbol')
          .from('out_of_stock', 'outOfStock')
          .from('get_notified', 'getNotified')
          .from('get_notified_text', 'getNotifiedText')
          .from('get_notified_modal', 'modal'),
      )
      .required(),
    defaultFreshRecipes: yup.array(yup.string()),
    bakedRecipesList: yup.array(
      itemSchema
        .from('display_name', 'title')
        .from('slug', 'value')
        .from('image', 'img')
        .from('unavailable_text', 'unavailableText')
        .from('show_more_details', 'showMoreDetails')
        .from('price_symbol', 'priceSymbol')
        .from('out_of_stock', 'outOfStock')
        .from('get_notified', 'getNotified')
        .from('get_notified_text', 'getNotifiedText')
        .from('get_notified_modal', 'modal'),
    ),
    defaultBakedRecipes: yup.array(yup.string()),
    mealPrepTypes: yup
      .array(
        itemSchema
          .from('display_name', 'title')
          .from('slug', 'value')
          .from('image', 'img')
          .from('unavailable_text', 'unavailableText')
          .from('show_more_details', 'showMoreDetails')
          .from('hidden', 'hidden'),
      )
      .required(),
    collapsedMealPrepTypes: yup
      .array(
        itemSchema
          .from('display_name', 'title')
          .from('slug', 'value')
          .from('image', 'img')
          .from('unavailable_text', 'unavailableText')
          .from('show_more_details', 'showMoreDetails')
          .from('hidden', 'hidden'),
      )
      .required(),
    mealPlanModalContent: yup.array(
      itemSchema
        .from('display_name', 'title')
        .from('slug', 'value')
        .from('image', 'img')
        .from('unavailable_text', 'unavailableText')
        .from('show_more_details', 'showMoreDetails')
        .from('hidden', 'hidden'),
    ),
    recipeSelectionText: yup.string(),
    callouts: yup.array(
      yup.object({
        icon: nullableImageFromCMSSchema,
        text: yup.string(),
      }),
    ),
  })
  .transform(value => {
    let question = null;
    if (value.question[0].component === 'flagship_ab_test') {
      question = useExperimentValues(value);
    } else {
      question = value.question[0];
    }
    return {
      ollieLogo: value.ollieLogo,
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      crunchingNumbersContentVariant: usePromoCodeVariant(
        question.crunchingNumbersContentVariants.map(variant => ({
          promoCode: variant.promoCode,
          content: variant.content[0],
        })),
      ),
      showPlumLoadingScreen: question.show_plum_loading_screen,
      headline: question.headline || 'Recommended for {{pupName}}',
      subheadline: question.subheadline,
      recipeModalsList:
        question.recipeModalsList ??
        value.question.find(q => q.recipeModalsList)?.recipeModalsList,
      recipesList: (question.recipes_list ?? []).filter(item => !item.hidden),
      defaultFreshRecipes: (question.default_fresh_recipes ?? [])
        .filter(item => !item.hidden && !item.out_of_stock)
        .map(item => item.slug),
      bakedRecipesList: (question.baked_recipes_list ?? []).filter(
        item => !item.hidden,
      ),
      defaultBakedRecipes: (question.default_baked_recipes ?? [])
        .filter(item => !item.hidden && !item.out_of_stock)
        .map(item => item.slug),
      mealPrepTypes: question.mealPlanTypes
        .filter(item => !item.hidden)
        .map(recipe => ({
          ...recipe,
          priceSymbol: recipe.price_symbol,
          pricingInfo: {
            price: recipe.price,
            discount: recipe.discount,
          },
        })),
      collapsedMealPrepTypes: question.collapsedMealPlanTypes
        .filter(item => !item.hidden)
        .map(recipe => ({
          ...recipe,
          priceSymbol: recipe.price_symbol,
          pricingInfo: {
            price: recipe.price,
            discount: recipe.discount,
          },
        })),
      body: question.body,
      ctaText: value.ctaButtonText || 'Continue',
      mealPlanModalContent: question.modalContent
        .filter(item => !item.hidden)
        .map(recipe => ({
          ...recipe,
          priceSymbol: recipe.price_symbol,
          pricingInfo: {
            price: recipe.price,
            discount: recipe.discount,
            period: recipe.period || 'week',
          },
        })),
      recipeSelectionText: question.recipe_selection_text,
      callouts: question.callouts,
    };
  });
